import { isDiarizationProvider } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Button, Group, Paper, Select, Stack, Switch, Text } from "@mantine/core"
import { SettingsReadOnlyNotice } from "../SettingsReadOnlyNotice"
import type { IProps, TStrictness } from "./interfaces"

// Match-strictness bands → the raw `speaker_confirm_threshold` a match must clear
// to auto-confirm. Backend default is 0.85 (Balanced); both neighbours keep the
// derived review/tentative bands valid (see backend deriveRecognitionBands).
const STRICTNESS_THRESHOLD: Record<TStrictness, string> = {
  relaxed: "0.80",
  balanced: "0.85",
  strict: "0.90",
}

const SETTING_KEYS = [
  "auto_identify_speakers",
  "echo_suppression_enabled",
  "speaker_confirm_threshold",
  "diarization_provider",
]

/**
 * Speaker-recognition tuning for a workspace — auto-identify, echo suppression,
 * match strictness, and which provider supplies speaker turns. Purely
 * presentational: the parent SettingsView threads the shared settings map +
 * save. Unset org keys resolve to "on / on / 0.85 / deepgram" (mirroring the
 * backend defaults), so an untouched workspace shows the real running
 * behaviour rather than a blank.
 */
export const SpeakerRecognitionCard = ({
  settings,
  saving,
  canManage = true,
  updateSetting,
  save,
}: IProps): React.JSX.Element => {
  const { i18n } = useLingui()

  // Unset resolves to the backend default (both switches ON), so absence reads
  // as "on" — only an explicit "false" turns a switch off.
  const autoIdentify = settings.auto_identify_speakers !== "false"
  const echoSuppression = settings.echo_suppression_enabled !== "false"

  const raw = Number(settings.speaker_confirm_threshold ?? "")
  const effective = Number.isFinite(raw) && raw > 0 && raw <= 1 ? raw : 0.85
  const strictness: TStrictness = effective >= 0.9 ? "strict" : effective <= 0.8 ? "relaxed" : "balanced"

  const rawDiarization = settings.diarization_provider ?? "deepgram"
  const diarizationProvider = isDiarizationProvider(rawDiarization) ? rawDiarization : "deepgram"

  return (
    <Paper withBorder p="md">
      <Stack gap="lg">
        <Group justify="space-between" wrap="nowrap" gap="md">
          <div>
            <Text fw={600}>
              <Trans>Recognize known voices automatically</Trans>
            </Text>
            <Text size="xs" c="dimmed">
              <Trans>
                Match speakers to people you've enrolled, so meetings label who's talking without you tagging them.
              </Trans>
            </Text>
          </div>
          <Switch
            checked={autoIdentify}
            disabled={!canManage}
            onChange={(e) => updateSetting("auto_identify_speakers", e.currentTarget.checked ? "true" : "false")}
            aria-label={i18n._(msg`Recognize known voices automatically`)}
          />
        </Group>

        <Group justify="space-between" wrap="nowrap" gap="md">
          <div>
            <Text fw={600}>
              <Trans>Echo suppression for two-channel recordings</Trans>
            </Text>
            <Text size="xs" c="dimmed">
              <Trans>
                When a room mic and a remote feed both capture the same voice, drop the duplicated words so a speaker
                isn't counted twice.
              </Trans>
            </Text>
          </div>
          <Switch
            checked={echoSuppression}
            disabled={!canManage}
            onChange={(e) => updateSetting("echo_suppression_enabled", e.currentTarget.checked ? "true" : "false")}
            aria-label={i18n._(msg`Echo suppression for two-channel recordings`)}
          />
        </Group>

        <div>
          <Select
            label={i18n._(msg`Match strictness`)}
            description={i18n._(msg`How sure the system must be before it confirms a voice match on its own.`)}
            data={[
              { value: "relaxed", label: i18n._(msg`Relaxed — identify more, risk the odd wrong name`) },
              { value: "balanced", label: i18n._(msg`Balanced — the default`) },
              { value: "strict", label: i18n._(msg`Strict — only very confident matches`) },
            ]}
            value={strictness}
            disabled={!canManage}
            onChange={(value) =>
              value && updateSetting("speaker_confirm_threshold", STRICTNESS_THRESHOLD[value as TStrictness])
            }
            allowDeselect={false}
            comboboxProps={{ withinPortal: true }}
            maw={420}
          />
          <Text size="xs" c="dimmed" mt={4}>
            {i18n._(msg`Confirms a voice match at ${effective.toFixed(2)} similarity or higher.`)}
          </Text>
        </div>

        <Select
          label={i18n._(msg`Speaker turns from`)}
          description={i18n._(
            msg`Soniox writes the words; the provider chosen here decides who spoke when. Azure LLM-speech scored best on speaker turns in our tests, AssemblyAI second. A second provider pass uses more credits per meeting.`,
          )}
          data={[
            { value: "deepgram", label: i18n._(msg`Deepgram (recommended)`) },
            { value: "mai", label: i18n._(msg`MAI-Transcribe-2 (preview)`) },
            { value: "azure-llm", label: i18n._(msg`Azure LLM-speech`) },
            { value: "assemblyai", label: i18n._(msg`AssemblyAI Universal-3.5 Pro`) },
            { value: "same", label: i18n._(msg`Same provider as the words`) },
          ]}
          value={diarizationProvider}
          disabled={!canManage}
          onChange={(value) => value && updateSetting("diarization_provider", value)}
          allowDeselect={false}
          comboboxProps={{ withinPortal: true }}
          maw={420}
        />

        {canManage ? (
          <Button onClick={() => save(SETTING_KEYS)} loading={saving} w="fit-content">
            <Trans>Save</Trans>
          </Button>
        ) : (
          <SettingsReadOnlyNotice />
        )}
      </Stack>
    </Paper>
  )
}
